/* iter55g13 — Convert Story to Theatrical Script dialog (Phase 2+3).
 *
 * Additions over the Phase 1 MVP:
 *   • Narrative-style toggle (Tell, Don't Show ↔ Show, Don't Tell).
 *   • Scene-beat sidebar (parsed slug lines with click-to-jump).
 *   • Character-Bible import from detected cast.
 *   • .fdx (Final Draft) and .pdf (screenwriter) exports.
 *   • Split-view novel ↔ script preview.
 *   • Editable fountain with revision snapshots and unified diff.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Download, Drama, FileText, Trash2, Clock,
  Users, Columns, History, Save, GitCompare, Film, MonitorPlay, Volume2, Tv,
  BookCopy, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { scriptsApi, chaptersApi, charactersApi } from '@/lib/api';
import ScriptReadThrough from './ScriptReadThrough';

const FALLBACK_FORMATS = [
  { key: 'stage_play',  label: 'Stage Play', brief: 'ACT / SCENE headers, centered character names, stage directions.' },
  { key: 'screenplay',  label: 'Screenplay', brief: 'Film format — INT./EXT. slug lines, action, dialogue, parentheticals.' },
  { key: 'teleplay',    label: 'Teleplay',   brief: 'TV episodic — TEASER + ACT ONE/TWO/THREE with commercial act-out beats.' },
  { key: 'radio_play',  label: 'Radio Play', brief: 'Audio-only — SFX cues, no visual action, dialogue-heavy.' },
];
const FALLBACK_STYLES = [
  { key: 'auto', label: 'Auto (match the format)',        brief: 'Stage → tell · Screenplay → show · Radio → balanced.' },
  { key: 'tell', label: "Tell, Don't Show (theatrical)",  brief: 'Dialogue-driven — characters SAY what they feel.' },
  { key: 'show', label: "Show, Don't Tell (cinematic)",   brief: 'Action-driven — characters ACT what they feel.' },
];

export default function ScriptConverterDialog({ open, onClose, project }) {
  // Meta.
  const [formats, setFormats]     = useState(FALLBACK_FORMATS);
  const [styles,  setStyles]      = useState(FALLBACK_STYLES);
  const [format, setFormat]       = useState('stage_play');
  const [style,  setStyle]        = useState('auto');

  // Conversion state.
  const [busy,       setBusy]       = useState(false);
  const [convertJob, setConvertJob] = useState(null);   // {job_id, status, chapters_total, chapters_done}
  const [history,    setHistory]    = useState([]);
  const [active,     setActive]     = useState(null);
  const [editing,    setEditing]    = useState(false);
  const [draft,      setDraft]      = useState('');   // editable fountain
  const [saving,     setSaving]     = useState(false);
  const [loading,    setLoading]    = useState(false);

  // Layout / helpers.
  const [splitView,   setSplit] = useState(false);
  const [chapters,    setChapters] = useState([]);
  const [revs,        setRevs]     = useState([]);
  const [diffText,    setDiffText] = useState('');
  const [diffFrom,    setDiffFrom] = useState('');
  const [diffTo,      setDiffTo]   = useState('current');
  const [readOpen,    setReadOpen] = useState(false);
  // Cast-import prompt: surfaces automatically when a conversion finishes
  // and the script has detectable cast members.
  const [showCastPrompt, setShowCastPrompt] = useState(false);
  const [castImporting,  setCastImporting]  = useState(false);
  // Series sync: shown after cast import to mark carryover characters.
  const [carryoverChars,    setCarryoverChars]    = useState([]);
  const [showSeriesSync,    setShowSeriesSync]    = useState(false);
  const [carryoverBusy,     setCarryoverBusy]     = useState({});   // {charId: bool}
  const [carryoverState,    setCarryoverState]    = useState({});   // {charId: bool}
  const preRef = useRef(null);
  const pollRef = useRef(null);

  // Poll active conversion job until done or error.
  const pollJob = useCallback(async (jobId) => {
    if (!jobId || !project?.id) return;
    try {
      const { job } = await scriptsApi.jobStatus(project.id, jobId);
      setConvertJob(job);
      if (job?.status === 'done') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setBusy(false);
        if (job.script_id) {
          const script = await scriptsApi.get(job.script_id);
          setActive(script);
          setHistory((prev) => [{ ...script, fountain: undefined }, ...prev]);
          toast.success(`Converted to ${formats.find(f => f.key === script.format)?.label || script.format}.`, {
            description: `${script.cast?.length || 0} cast · ${script.scenes?.length || script.scene_count || 0} scenes`,
          });
          // Auto-prompt character import when cast was detected
          if (script.cast?.length > 0) setShowCastPrompt(true);
        }
      } else if (job?.status === 'error') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setBusy(false);
        toast.error('Conversion failed.', { description: job.error || 'Try again in a moment.' });
      }
    } catch { /* silent — will retry on next tick */ }
  }, [project?.id, formats]);

  // Clear poll on unmount / close.
  useEffect(() => {
    if (!open) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [open]);

  // Hydrate meta + history on open.
  useEffect(() => {
    if (!open || !project?.id) return;
    setLoading(true);
    Promise.all([
      scriptsApi.meta().catch(() => null),
      scriptsApi.list(project.id).catch(() => []),
      chaptersApi.list(project.id).catch(() => []),
    ]).then(([meta, rows, chs]) => {
      if (meta?.formats?.length)          setFormats(meta.formats);
      if (meta?.narrative_styles?.length) setStyles(meta.narrative_styles);
      setHistory(rows || []);
      setChapters(chs || []);
    }).finally(() => setLoading(false));
  }, [open, project?.id]);

  // When the active script changes, hydrate its revisions.
  useEffect(() => {
    if (!active?.id) { setRevs([]); return; }
    scriptsApi.revisions(active.id).then(setRevs).catch(() => setRevs([]));
    setDraft(active.fountain || '');
    setEditing(false);
    setDiffText('');
  }, [active]);

  const runConvert = async () => {
    setBusy(true);
    setConvertJob(null);
    try {
      const res = await scriptsApi.convert(project.id, { format, narrative_style: style });
      // res = { job_id, status: 'queued', chapters_total }
      setConvertJob({ job_id: res.job_id, status: res.status, chapters_total: res.chapters_total, chapters_done: 0 });
      toast.info('Conversion started — working through chapters…', {
        description: `${res.chapters_total || '?'} chapters queued.`,
      });
      // Start polling.
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => pollJob(res.job_id), 4000);
    } catch (e) {
      setBusy(false);
      toast.error('Conversion failed.', {
        description: e?.response?.data?.detail || 'Try again in a moment.',
      });
    }
  };

  const openHistory = async (sid) => {
    setLoading(true);
    try {
      setActive(await scriptsApi.get(sid));
    } catch {
      toast.error('Could not load that script.');
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (sid) => {
    try {
      await scriptsApi.remove(sid);
      setHistory((prev) => prev.filter((s) => s.id !== sid));
      if (active?.id === sid) setActive(null);
      toast.success('Deleted script version.');
    } catch {
      toast.error('Could not delete.');
    }
  };

  const saveEdit = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const updated = await scriptsApi.update(active.id, { fountain: draft, note: 'Manual edit' });
      setActive(updated);
      setEditing(false);
      const nextRevs = await scriptsApi.revisions(active.id);
      setRevs(nextRevs);
      toast.success('Saved. Previous version snapshotted to Revisions.');
    } catch (e) {
      toast.error('Save failed.', { description: e?.response?.data?.detail || '' });
    } finally {
      setSaving(false);
    }
  };

  const importCast = async () => {
    if (!active?.id) return;
    setCastImporting(true);
    try {
      const r = await scriptsApi.importCast(active.id);
      toast.success(`Imported ${r.created.length} character${r.created.length === 1 ? '' : 's'} to the Bible.`, {
        description: r.skipped.length ? `${r.skipped.length} already existed and were skipped.` : undefined,
      });
      setShowCastPrompt(false);
      // Open series sync panel when characters were created
      if (r.created?.length > 0) {
        setCarryoverChars(r.created);
        setCarryoverState(Object.fromEntries(r.created.map((c) => [c.id, false])));
        setCarryoverBusy({});
        setShowSeriesSync(true);
      }
    } catch (e) {
      toast.error('Cast import failed.', { description: e?.response?.data?.detail || '' });
    } finally {
      setCastImporting(false);
    }
  };

  const toggleCarryover = async (charId, val) => {
    setCarryoverBusy((prev) => ({ ...prev, [charId]: true }));
    try {
      await charactersApi.seriesCarryover(charId, val);
      setCarryoverState((prev) => ({ ...prev, [charId]: val }));
    } catch {
      toast.error('Could not update carryover — try again.');
    } finally {
      setCarryoverBusy((prev) => ({ ...prev, [charId]: false }));
    }
  };

  const showDiff = async (rid) => {
    if (!active?.id) return;
    try {
      const r = await scriptsApi.diff(active.id, rid, diffTo || 'current');
      setDiffFrom(rid);
      setDiffText(r.diff || '(no changes)');
    } catch (e) {
      toast.error('Could not compute diff.');
    }
  };

  const jumpToScene = (line) => {
    if (!preRef.current) return;
    const el = preRef.current;
    // Approximate line-height (px) — pre uses a monospace 11px/1.65.
    const approxLh = 18;
    el.scrollTo({ top: Math.max(0, (line - 1) * approxLh - 40), behavior: 'smooth' });
  };

  const novelText = useMemo(() => {
    if (!active || !chapters?.length) return '';
    const chosen = new Set(active.chapter_ids || []);
    return chapters
      .filter((c) => (chosen.size ? chosen.has(c.id) : true) && (c.content || '').trim())
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((c) => `# ${c.title || `Chapter ${c.number || ''}`}\n\n${c.content}`)
      .join('\n\n\n');
  }, [active, chapters]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden"
        data-testid="script-converter-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Drama className="h-4 w-4 text-primary" />
            Convert this novel to a theatrical script
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Claude will adapt your chapters into a Fountain-format script.
            Pick a format + narrative style, generate, then download as{' '}
            <b>.fountain</b>, <b>.docx</b>, <b>.fdx</b> (Final Draft), or a{' '}
            screenwriter <b>.pdf</b>.
          </DialogDescription>
        </DialogHeader>

        {/* Format picker */}
        <div className="grid gap-2 md:grid-cols-4">
          {formats.map((f) => {
            const Icon = f.key === 'screenplay' ? Film
              : f.key === 'teleplay'  ? Tv
              : f.key === 'radio_play' ? Volume2
              : Drama;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFormat(f.key)}
                data-testid={`script-format-${f.key}`}
                className={
                  'rounded-md border p-2 text-left text-[11.5px] transition-colors '
                  + (format === f.key
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-panel-border bg-card hover:bg-secondary')
                }
              >
                <div className="flex items-center gap-1 text-[13px] font-semibold">
                  <Icon className="h-3.5 w-3.5" />
                  {f.label}
                </div>
                <div className="mt-0.5 line-clamp-2 text-[10.5px] text-muted-foreground">{f.brief}</div>
              </button>
            );
          })}
        </div>

        {/* Narrative-style picker */}
        <div className="grid gap-2 md:grid-cols-3" data-testid="narrative-style-row">
          {styles.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setStyle(s.key)}
              data-testid={`narrative-style-${s.key}`}
              className={
                'rounded-md border p-2 text-left text-[11.5px] transition-colors '
                + (style === s.key
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-panel-border bg-card hover:bg-secondary')
              }
            >
              <div className="flex items-center gap-1 text-[12px] font-semibold">
                {s.key === 'show' ? <MonitorPlay className="h-3 w-3" />
                  : s.key === 'tell' ? <Drama className="h-3 w-3" />
                  : <Film className="h-3 w-3" />}
                {s.label}
              </div>
              <div className="mt-0.5 line-clamp-2 text-[10.5px] text-muted-foreground">{s.brief}</div>
            </button>
          ))}
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Button
            onClick={runConvert}
            disabled={busy || !project?.id}
            data-testid="script-convert-btn"
          >
            {busy
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            {busy ? 'Converting…' : 'Convert now'}
          </Button>
          {busy && convertJob && (
            <span className="text-[10.5px] text-muted-foreground" data-testid="script-job-progress">
              {convertJob.status === 'queued' ? 'Queued…'
                : `${convertJob.chapters_done || 0} / ${convertJob.chapters_total || '?'} chapters`}
            </span>
          )}
          {!busy && (
            <span className="text-[10.5px] text-muted-foreground">
              Runs in the background — you can keep writing while it converts.
            </span>
          )}
          {active && (
            <button
              type="button"
              onClick={() => setSplit((v) => !v)}
              data-testid="script-splitview-toggle"
              className={
                'ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] '
                + (splitView
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-panel-border bg-card hover:bg-secondary')
              }
              title="Show source novel side-by-side"
            >
              <Columns className="h-3 w-3" /> Split view
            </button>
          )}
        </div>

        {/* Version history */}
        {history.length > 0 && (
          <div className="mt-1 rounded-md border border-panel-border bg-card p-2" data-testid="script-history">
            <div className="mb-1 flex items-center gap-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <Clock className="h-3 w-3" /> Previous versions
            </div>
            <ul className="space-y-1">
              {history.slice(0, 6).map((s) => (
                <li key={s.id} className="flex items-center gap-2 rounded-sm px-2 py-1 hover:bg-secondary">
                  <button
                    type="button"
                    onClick={() => openHistory(s.id)}
                    className="flex-1 text-left text-[11.5px]"
                    data-testid={`script-history-open-${s.id}`}
                  >
                    <div>{s.title || '(untitled)'} · {formats.find((f) => f.key === s.format)?.label || s.format}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(s.created_at).toLocaleString()} · {s.cast?.length || 0} cast · {s.scenes?.length || s.scene_count || 0} scenes
                    </div>
                  </button>
                  <a href={scriptsApi.exportUrl(s.id, 'fountain')} download className="rounded-sm p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary" title="Download .fountain">
                    <FileText className="h-3.5 w-3.5" />
                  </a>
                  <a href={scriptsApi.exportUrl(s.id, 'docx')} download className="rounded-sm p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary" title="Download .docx">
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => deleteEntry(s.id)}
                    className="rounded-sm p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    title="Delete"
                    data-testid={`script-history-delete-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Active script workbench */}
        {active && (
          <div className="mt-1 flex flex-1 flex-col overflow-hidden rounded-md border border-panel-border bg-card" data-testid="script-preview">
            {/* Cast-import auto-prompt — shown once after conversion completes */}
            {showCastPrompt && active.cast?.length > 0 && (
              <div className="flex items-center gap-2 border-b border-emerald-500/20 bg-emerald-500/8 px-3 py-2" data-testid="script-cast-import-banner">
                <Users className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <span className="flex-1 text-[11.5px] text-emerald-800 dark:text-emerald-300">
                  <strong>{active.cast.length} character{active.cast.length === 1 ? '' : 's'}</strong> detected:{' '}
                  {active.cast.slice(0, 5).join(', ')}{active.cast.length > 5 ? ` + ${active.cast.length - 5} more` : ''}.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 gap-1 border-emerald-500/40 text-[11px] text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300"
                  onClick={importCast}
                  disabled={castImporting}
                  data-testid="script-cast-import-btn"
                >
                  {castImporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users className="h-3 w-3" />}
                  Add to Character Bible
                </Button>
                <button
                  type="button"
                  onClick={() => setShowCastPrompt(false)}
                  className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss"
                  data-testid="script-cast-import-dismiss"
                >✕</button>
              </div>
            )}
            {/* ── Series Sync panel — shown after cast import ── */}
            {showSeriesSync && carryoverChars.length > 0 && (
              <div className="border-b border-blue-500/20 bg-blue-500/5 px-3 py-2" data-testid="series-sync-panel">
                <div className="mb-2 flex items-center gap-2">
                  <BookCopy className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <span className="flex-1 text-[11.5px] font-semibold text-blue-800 dark:text-blue-300">
                    Series Sync — mark characters who carry over to Book 2
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowSeriesSync(false)}
                    className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                    aria-label="Dismiss"
                    data-testid="series-sync-dismiss"
                  >✕</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {carryoverChars.map((c) => {
                    const on = carryoverState[c.id] ?? false;
                    const busy2 = carryoverBusy[c.id] ?? false;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => !busy2 && toggleCarryover(c.id, !on)}
                        disabled={busy2}
                        data-testid={`series-sync-toggle-${c.id}`}
                        title={on ? 'Click to remove Book 2 carryover' : 'Click to mark as Book 2 carryover'}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                          on
                            ? 'border-blue-500/60 bg-blue-500/15 text-blue-700 dark:text-blue-300'
                            : 'border-panel-border bg-background text-muted-foreground hover:bg-secondary'
                        }`}
                      >
                        {busy2
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : on
                            ? <CheckCircle2 className="h-3 w-3" />
                            : <BookCopy className="h-3 w-3" />
                        }
                        {c.name || 'Unnamed'}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[10.5px] text-muted-foreground">
                  Ticked characters will show a "Book 2+" badge in the Character Bible.
                </p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1 border-b border-panel-border bg-panel-header px-2 py-1.5">
              <span className="text-[11.5px] font-semibold">
                {active.title} · {formats.find((f) => f.key === active.format)?.label || active.format}
              </span>
              <span className="text-[10.5px] text-muted-foreground">
                · {active.cast?.length || 0} cast · {active.scenes?.length || active.scene_count || 0} scenes
              </span>
              <div className="ml-auto flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setReadOpen(true)}
                  className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] hover:bg-primary/10"
                  data-testid="script-readthrough-open"
                  title="Play a cast-voiced read-through of this script"
                >
                  <Volume2 className="h-3 w-3" /> Read-through
                </button>
                <button
                  type="button"
                  onClick={importCast}
                  disabled={!active.cast?.length}
                  className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] hover:bg-primary/10 disabled:opacity-50"
                  data-testid="script-import-cast"
                  title="Add these characters to the project's Character Bible"
                >
                  <Users className="h-3 w-3" /> Import cast
                </button>
                <a href={scriptsApi.exportUrl(active.id, 'fountain')} download className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] hover:bg-primary/10" data-testid="script-download-fountain">
                  <FileText className="h-3 w-3" /> .fountain
                </a>
                <a href={scriptsApi.exportUrl(active.id, 'docx')} download className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] hover:bg-primary/10" data-testid="script-download-docx">
                  <Download className="h-3 w-3" /> .docx
                </a>
                <a href={scriptsApi.exportUrl(active.id, 'fdx')} download className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] hover:bg-primary/10" data-testid="script-download-fdx">
                  <Film className="h-3 w-3" /> .fdx
                </a>
                <a href={scriptsApi.exportUrl(active.id, 'pdf')} download className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10.5px] hover:bg-primary/10" data-testid="script-download-pdf">
                  <FileText className="h-3 w-3" /> PDF
                </a>
              </div>
            </div>

            <div className="grid flex-1 grid-cols-12 overflow-hidden">
              {/* Scene beat sidebar */}
              <div className="col-span-3 max-h-[45vh] overflow-y-auto border-r border-panel-border bg-background/40 p-2" data-testid="script-scenes">
                <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <Drama className="h-3 w-3" /> Scenes ({active.scenes?.length || 0})
                </div>
                {(active.scenes || []).length === 0 && (
                  <div className="text-[10.5px] italic text-muted-foreground">
                    No scenes parsed — re-convert with more explicit chapters.
                  </div>
                )}
                <ol className="space-y-0.5">
                  {(active.scenes || []).map((sc) => (
                    <li key={`${sc.index}-${sc.line}`}>
                      <button
                        type="button"
                        onClick={() => jumpToScene(sc.line)}
                        data-testid={`script-scene-${sc.index}`}
                        className="w-full rounded-sm px-1.5 py-0.5 text-left text-[10.5px] hover:bg-primary/10 hover:text-primary"
                      >
                        <span className="mr-1 font-semibold">{sc.index}.</span>
                        {sc.heading}
                      </button>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Preview / edit body */}
              <div className={`${splitView ? 'col-span-5' : 'col-span-9'} flex flex-col overflow-hidden`}>
                <div className="flex items-center gap-1 border-b border-panel-border bg-background/40 px-2 py-1">
                  <button
                    type="button"
                    onClick={() => setEditing((v) => !v)}
                    data-testid="script-edit-toggle"
                    className={
                      'inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10.5px] '
                      + (editing ? 'border-primary bg-primary/10 text-primary' : 'border-panel-border hover:bg-secondary')
                    }
                  >
                    {editing ? 'Preview' : 'Edit'}
                  </button>
                  {editing && (
                    <Button
                      size="sm"
                      onClick={saveEdit}
                      disabled={saving || draft === (active.fountain || '')}
                      data-testid="script-save-edit"
                      className="h-6 gap-1 px-2 text-[10.5px]"
                    >
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      Save & snapshot
                    </Button>
                  )}
                  <span className="ml-auto text-[10px] text-muted-foreground">Fountain source</span>
                </div>
                {editing ? (
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    data-testid="script-edit-textarea"
                    className="min-h-[36vh] w-full flex-1 resize-none bg-background p-3 text-[11px] outline-none"
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.65 }}
                    spellCheck
                  />
                ) : (
                  <pre
                    ref={preRef}
                    className="min-h-[36vh] flex-1 overflow-auto bg-background p-3 text-[11px] leading-relaxed"
                    data-testid="script-preview-body"
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap', lineHeight: 1.65 }}
                  >
                    {active.fountain}
                  </pre>
                )}
              </div>

              {/* Split-view: novel source */}
              {splitView && (
                <div className="col-span-4 flex flex-col overflow-hidden border-l border-panel-border">
                  <div className="border-b border-panel-border bg-background/40 px-2 py-1 text-[10.5px] font-semibold">
                    Source novel
                  </div>
                  <pre
                    className="min-h-[36vh] flex-1 overflow-auto bg-background p-3 text-[11px] leading-relaxed"
                    data-testid="script-split-novel"
                    style={{ fontFamily: 'Georgia, "Iowan Old Style", serif', whiteSpace: 'pre-wrap' }}
                  >
                    {novelText || '(load a script — chapters will render here)'}
                  </pre>
                </div>
              )}
            </div>

            {/* Revisions */}
            {revs.length > 0 && (
              <div className="border-t border-panel-border bg-background/40 p-2" data-testid="script-revisions">
                <div className="mb-1 flex items-center gap-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <History className="h-3 w-3" /> Revisions ({revs.length})
                  <span className="ml-auto text-[10px] normal-case text-muted-foreground">
                    Compare to:
                    <select
                      value={diffTo}
                      onChange={(e) => setDiffTo(e.target.value)}
                      className="ml-1 rounded-sm border border-panel-border bg-card px-1 py-0.5 text-[10px]"
                      data-testid="script-diff-target"
                    >
                      <option value="current">Current draft</option>
                      {revs.map((r) => (
                        <option key={r.id} value={r.id}>{new Date(r.created_at).toLocaleString()}</option>
                      ))}
                    </select>
                  </span>
                </div>
                <div className="flex max-h-24 flex-wrap gap-1 overflow-auto">
                  {revs.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => showDiff(r.id)}
                      data-testid={`script-revision-diff-${r.id}`}
                      className={
                        'inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] '
                        + (diffFrom === r.id
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-panel-border hover:bg-secondary')
                      }
                    >
                      <GitCompare className="h-2.5 w-2.5" />
                      {new Date(r.created_at).toLocaleString()}
                      {r.note ? ` · ${r.note}` : ''}
                    </button>
                  ))}
                </div>
                {diffText && (
                  <pre
                    className="mt-1 max-h-40 overflow-auto rounded-sm bg-black/70 p-2 text-[10px] leading-snug text-emerald-200"
                    data-testid="script-diff-output"
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                  >
                    {diffText.split('\n').map((line, i) => (
                      <div
                        key={i}
                        className={
                          line.startsWith('+') && !line.startsWith('+++') ? 'text-emerald-300' :
                          line.startsWith('-') && !line.startsWith('---') ? 'text-rose-300' :
                          line.startsWith('@@') ? 'text-sky-300' : 'text-neutral-300'
                        }
                      >
                        {line || ' '}
                      </div>
                    ))}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}

        {loading && !active && (
          <div className="grid place-items-center py-4 text-[11.5px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy || saving}>Close</Button>
        </DialogFooter>
      </DialogContent>
      <ScriptReadThrough
        open={readOpen}
        onClose={() => setReadOpen(false)}
        script={active}
      />
    </Dialog>
  );
}
